import {
    BuildDiffResult,
    CalculationSummary,
    RowDiffMap,
    TaxDiffDiscrepanciesFlags,
    TaxDiffMap,
    TotalsDiffFlags,
} from './types';
import { getCurrencyData } from 'utils/common';

const toCents = (value: number) => Math.round((value + Number.EPSILON) * 100);

export const nearlyEqual = (a?: number, b?: number) => {
    if (a == null && b == null) return true;
    if (a == null || b == null) return false;

    return toCents(a) === toCents(b);
};

export const makeTaxKey = (itemIndex: string, taxType: string) => `${itemIndex}|${taxType}`;

export const buildTotalsCalculationsDiff = (
    local: CalculationSummary,
    revenue: CalculationSummary
): TotalsDiffFlags => {
    const totalsDiff: TotalsDiffFlags = {};

    // Compare the totals diffs
    if (!nearlyEqual(local.totalVat, revenue.totalVat)) totalsDiff.totalVat = true;
    if (!nearlyEqual(local.totalDuty, revenue.totalDuty)) totalsDiff.totalDuty = true;
    if (!nearlyEqual(local.totalLiability, revenue.totalLiability)) totalsDiff.totalLiability = true;

    return totalsDiff;
};

export const buildRowsCalculationsDiffMap = (
    local: CalculationSummary,
    revenue: CalculationSummary
): BuildDiffResult => {
    const taxDiffMap: TaxDiffMap = {};
    const rowDiffMap: RowDiffMap = {};

    const localTaxMap = new Map<string, { taxBase: number; taxRate: number; assessedAmount: number }>();

    for (const row of local.rows) {
        for (const tax of row.taxes) {
            localTaxMap.set(makeTaxKey(row.index, tax.taxType), {
                taxBase: tax.taxBase,
                taxRate: tax.taxRate,
                assessedAmount: tax.assessedAmount,
            });
        }
    }

    // Compare revenue to local and mark missing ones
    for (const rRow of revenue.rows) {
        const lRow = local.rows.find((l) => l.index === rRow.index);

        // Row-level diff
        if (!lRow || !nearlyEqual(lRow.totalLiability, rRow.totalLiability)) {
            rowDiffMap[rRow.index] = { totalLiability: true };
        }

        // Tax-level diff
        for (const tax of rRow.taxes) {
            const key = makeTaxKey(rRow.index, tax.taxType);
            const localTax = localTaxMap.get(key);

            if (!localTax) {
                taxDiffMap[key] = {
                    taxBase: true,
                    taxRate: true,
                    assessedAmount: true,
                };
                continue;
            }

            const diff: TaxDiffDiscrepanciesFlags = {};

            if (!nearlyEqual(localTax.taxBase, tax.taxBase)) diff.taxBase = true;
            if (!nearlyEqual(localTax.taxRate, tax.taxRate)) diff.taxRate = true;
            if (!nearlyEqual(localTax.assessedAmount, tax.assessedAmount)) diff.assessedAmount = true;

            // Only store this taxLine to the diff map if there is at least one difference
            if (Object.keys(diff).length > 0) taxDiffMap[key] = diff;
        }
    }

    return {
        taxDiffMap,
        rowDiffMap,
    };
};

const { currencySymbol } = getCurrencyData();

export const formatValue = (value: number): string => {
    return `${currencySymbol} ${value.toFixed(2)}`;
};

export const getTaxPriority = (t?: string | null) => {
    if (!t) return 99;
    if (t === '1B2') return 3; // deferred VAT last
    if (t === 'B00') return 2; // VAT after duties
    return 1; // duties first (A00, A10, etc.)
};
